import Blue from "~/core";
import asyncScroll from "~/utils/_asyncScroll";

new Blue({
  name: "Checkout",
  selector: ".checkout__container, .product-added-container",
  setup(container) {
    const findCurrentStep = () => {
      try {
        const activeHeader = document.querySelector(
          ".checkout__section-header.active"
        );
        if (activeHeader) {
          return activeHeader.closest(".checkout__section");
        }

        // Fallback: handle steps without headers (e.g., Review)
        const placeOrderBtn = document.querySelector(
          "#placeOrderBtn:not([disabled])"
        );
        if (placeOrderBtn) {
          const section = placeOrderBtn.closest(".checkout__section");
          if (section) return section;
          const container = placeOrderBtn.closest(
            ".checkout__section-container"
          );
          if (container)
            return container.closest(".checkout__section") || container;
        }
      } catch (error) {
        console.warn("Error finding current checkout step:", error);
      }

      return null;
    };

    const scrollToStep = async (stepElement) => {
      try {
        const header = stepElement.querySelector(".checkout__section-header");

        if (header) {
          const headerRect = header.getBoundingClientRect();

          const isMobile = window.innerWidth < 768; // md breakpoint

          let offset = isMobile ? 110 : 100;

          const scrollTop = window.pageYOffset + headerRect.top - offset;

          await asyncScroll({
            method: "scrollTo",
            top: scrollTop,
          });
        } else {
          // No header present: scroll to top of section/container
          const container =
            stepElement.querySelector(".checkout__section-container") ||
            stepElement;
          const rect = container.getBoundingClientRect();
          const isMobile = window.innerWidth < 768; // md breakpoint
          let offset = isMobile ? 110 : 100;
          const scrollTop = window.pageYOffset + rect.top - offset;
          await asyncScroll({
            method: "scrollTo",
            top: scrollTop,
          });
        }
      } catch (error) {
        console.warn("Error scrolling to checkout step:", error);
      }
    };

    const setupPaymentFrame = () => {
      const paymentFrame = document.getElementById("paymentFrame");

      if (paymentFrame) {
        paymentFrame.addEventListener("load", paymentFrameCompleted);
      }
    };

    const paymentFrameCompleted = () => {
      const paymentFrame = document.getElementById("paymentFrame");
      let currentDomain = window.location.origin;

      if (paymentFrame && paymentFrame.contentDocument) {
        const frameLocation = paymentFrame.contentDocument.location.href;

        if (window.location.pathname.length > 1) {
          currentDomain = currentDomain.replace(window.location.pathname, "");
        }

        if (frameLocation.startsWith(currentDomain)) {
          cartAction(
            "add_payment_info",
            { payment_type: "Credit Card" },
            cartProducts
          );

          const form = document.getElementById("paymentMethodForm");
          if (form) {
            form.submit();
          }
        }
      }
    };

    const setupOrderSubmission = () => {
      const form = document.getElementById("submitOrderForm");
      const button = document.getElementById("placeOrderBtn");

      if (form && button) {
        form.addEventListener("submit", function (e) {
          try {
            if (
              typeof window.cartAction === "function" &&
              window.cartProducts
            ) {
              const currency = form.getAttribute("data-currency");
              const value = parseFloat(form.getAttribute("data-value") || "0");
              const tax = parseFloat(form.getAttribute("data-tax") || "0");
              const shipping = parseFloat(
                form.getAttribute("data-shipping") || "0"
              );
              const transaction = form.getAttribute("data-transaction");

              window.cartAction(
                "purchase",
                {
                  currency: currency || "USD",
                  value: isNaN(value) ? 0 : value,
                  tax: isNaN(tax) ? 0 : tax,
                  shipping: isNaN(shipping) ? 0 : shipping,
                  transaction_id: transaction || "",
                },
                window.cartProducts
              );
            }
          } catch (err) {
            console.warn("Error firing purchase cartAction:", err);
          }

          showLoadingOverlay("Placing your order...");

          button.style.display = "none";
        });
      }
    };

    const currentStep = findCurrentStep();

    if (currentStep) {
      const allSections = document.querySelectorAll(".checkout__section");
      const isFirstStep = allSections[0] === currentStep;

      if (!isFirstStep) {
        setTimeout(() => {
          scrollToStep(currentStep);
        }, 100);
      }
    }

    // Setup payment frame handling
    setupPaymentFrame();

    // Setup order submission handling
    setupOrderSubmission();

    const setupCheckoutStepLoading = () => {
      const checkoutButtons = container.querySelectorAll("button.checkout-btn");

      checkoutButtons.forEach((button) => {
        const form = button.closest("form");

        if (form && form.id !== "submitOrderForm") {
          form.addEventListener("submit", function (e) {
            showLoadingOverlay("Processing your information...");
            button.disabled = true;
          });
        }
      });
    };

    const showLoadingOverlay = (message = "Loading...") => {
      const existingOverlay = document.querySelector(
        ".checkout-loading-overlay"
      );
      if (existingOverlay) {
        existingOverlay.remove();
      }

      const overlay = document.createElement("div");
      overlay.className = "checkout-loading-overlay";
      overlay.innerHTML = `
        <div class="spinner-container">
          <div class="spinner"></div>
          <p class="spinner-text">${message}</p>
        </div>
      `;

      document.body.appendChild(overlay);
      requestAnimationFrame(() => {
        overlay.classList.add("active");
      });
    };

    setupCheckoutStepLoading();

    const setupPaymentStepLoading = () => {
      const paymentFrame = document.getElementById("paymentFrame");

      if (paymentFrame) {
        paymentFrame.addEventListener("load", function () {
          try {
            const frameUrl = this.contentWindow?.location?.href;
            if (frameUrl && frameUrl.includes(window.location.origin)) {
              showLoadingOverlay("Processing payment method...");
            }
          } catch (e) {
            // Cross-origin access - this is expected
          }
        });
      }
    };

    setupPaymentStepLoading();

    if (!window.cartProducts) {
      window.cartProducts = [];
    }

    const hydrateCartProductsFromDataAttrs = () => {
      try {
        const holders = document.querySelectorAll("[data-ga4-products]");
        if (!holders || holders.length === 0) return;

        const items = document.querySelectorAll("[data-ga4-item]");
        if (!items || items.length === 0) return;

        const maybePush = (el) => {
          const name = el.getAttribute("data-name") || "";
          const id = el.getAttribute("data-id") || "";
          const brand = el.getAttribute("data-brand") || "";
          const cat = el.getAttribute("data-cat") || "";
          const subcat = el.getAttribute("data-subcat") || "";
          const priceStr = el.getAttribute("data-price") || "0";
          const qtyStr = el.getAttribute("data-qty") || "0";
          const price = parseFloat(priceStr);
          const qty = parseInt(qtyStr, 10);

          if (typeof window.ga4Product === "function") {
            window.cartProducts.push(
              window.ga4Product(
                name,
                id,
                brand,
                cat,
                subcat,
                isNaN(price) ? 0 : price,
                isNaN(qty) ? 0 : qty
              )
            );
          }
        };

        items.forEach(maybePush);
      } catch (err) {
        console.warn("GA4 hydrate error:", err);
      }
    };

    hydrateCartProductsFromDataAttrs();

    window.selectShipping = function (choice) {
      try {
        const checked = choice.querySelector("input[type=radio]:checked");
        if (
          checked &&
          typeof window.cartAction === "function" &&
          window.cartProducts
        ) {
          window.cartAction(
            "add_shipping_info",
            { shipping_tier: checked.value },
            window.cartProducts
          );
        }
      } catch (err) {
        console.warn("selectShipping error:", err);
      }
    };

    const setupShippingOptions = () => {
      const radioButtons = document.querySelectorAll(
        'input[name="SelectedShippingOption"]'
      );
      radioButtons.forEach((radio) => {
        radio.addEventListener("change", function () {
          if (this.checked && window.selectShipping) {
            const container =
              this.closest(".checkout__section-container") || document;
            window.selectShipping(container);
          }
        });
      });
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", setupShippingOptions);
    } else {
      setupShippingOptions();
    }
  },
});
